import { useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ScrollToTop from '@/components/feature/ScrollToTop';
import { comics } from '@/mocks/comics';

export default function ComicDetail() {
  const { id } = useParams<{ id: string }>();
  const comic = comics.find((c) => c.id === id);
  const [showAllChapters, setShowAllChapters] = useState(false);

  if (!comic) {
    return (
      <>
        <ScrollToTop />
        <Navbar />
        <main className="min-h-screen bg-background-50 pt-24 flex items-center justify-center">
          <div className="text-center">
            <div className="w-20 h-20 mx-auto mb-4 rounded-full bg-background-100 flex items-center justify-center">
              <i className="ri-emotion-sad-line text-3xl text-foreground-400"></i>
            </div>
            <h2 className="text-xl font-heading font-bold text-foreground-800 mb-2">漫画未找到</h2>
            <p className="text-sm text-foreground-500 mb-4">抱歉，您要查看的漫画不存在或已下架</p>
            <Link to="/" className="text-sm text-primary-500 hover:text-primary-600 font-medium">
              返回首页
            </Link>
          </div>
        </main>
        <Footer />
      </>
    );
  }

  const related = comics.filter(c => c.categorySlug === comic.categorySlug && c.id !== comic.id).slice(0, 4);
  const displayedChapters = showAllChapters ? comic.chapters : comic.chapters.slice(0, 10);

  return (
    <>
      <ScrollToTop />
      <Navbar />
      <main className="min-h-screen bg-background-50">
        <section className="pt-24 pb-8 md:pt-28 md:pb-12 bg-background-100">
          <div className="max-w-screen-2xl mx-auto px-4 md:px-6">
            <nav className="flex items-center gap-2 text-sm text-foreground-500 mb-6 flex-wrap" aria-label="面包屑导航">
              <Link to="/" className="hover:text-primary-500 transition-colors whitespace-nowrap">首页</Link>
              <i className="ri-arrow-right-s-line text-xs"></i>
              <Link to={`/category/${comic.categorySlug}`} className="hover:text-primary-500 transition-colors whitespace-nowrap">{comic.category}</Link>
              <i className="ri-arrow-right-s-line text-xs"></i>
              <span className="text-foreground-800 font-medium truncate">{comic.title}</span>
            </nav>

            <div className="flex flex-col md:flex-row gap-8 md:gap-10">
              <div className="flex-shrink-0 w-40 md:w-56">
                <div className="aspect-[3/4] rounded-lg overflow-hidden bg-background-200">
                  <img src={comic.cover} alt={comic.title} className="w-full h-full object-cover" />
                </div>
              </div>

              <div className="flex-1 min-w-0">
                <h1 className="text-xl md:text-3xl font-heading font-bold text-foreground-950 mb-3">
                  {comic.title}
                </h1>
                <div className="flex flex-wrap items-center gap-3 mb-4">
                  <span className="text-sm text-foreground-600">作者：{comic.author}</span>
                  <span className="w-px h-4 bg-background-300"></span>
                  <span className="flex items-center gap-1 text-sm text-accent-600">
                    <i className="ri-star-fill text-accent-400"></i>
                    {comic.rating} 分
                  </span>
                  <span className="w-px h-4 bg-background-300"></span>
                  <span className="text-sm text-foreground-600">{comic.views.toLocaleString()} 次阅读</span>
                  <span className="w-px h-4 bg-background-300"></span>
                  <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${
                    comic.status === '连载中'
                      ? 'bg-primary-100 text-primary-700'
                      : 'bg-secondary-100 text-secondary-700'
                  }`}>
                    {comic.status}
                  </span>
                </div>

                <div className="flex flex-wrap gap-2 mb-4">
                  {comic.tags.map((tag) => (
                    <span key={tag} className="px-3 py-1 rounded-full bg-background-100 text-xs text-foreground-600 border border-background-200/70">
                      {tag}
                    </span>
                  ))}
                </div>

                <p className="text-sm text-foreground-600 leading-relaxed mb-6">
                  {comic.description}
                </p>

                <Link
                  to={`/comic/${comic.id}`}
                  className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-primary-500 text-background-50 font-medium text-sm hover:bg-primary-600 transition-all whitespace-nowrap"
                >
                  <i className="ri-play-circle-line"></i>
                  开始阅读 第1话
                </Link>
              </div>
            </div>
          </div>
        </section>

        <section className="py-8 md:py-12">
          <div className="max-w-screen-2xl mx-auto px-4 md:px-6">
            <div className="flex flex-col lg:flex-row gap-8">
              <div className="flex-1">
                <h2 className="text-lg font-heading font-bold text-foreground-950 mb-5">
                  章节列表 <span className="text-sm font-normal text-foreground-500">（共 {comic.chapters.length} 话）</span>
                </h2>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                  {displayedChapters.map((ch) => (
                    <Link
                      key={ch.id}
                      to={`/comic/${comic.id}`}
                      className="flex items-center justify-between p-3.5 rounded-lg bg-background-100 hover:bg-background-200/70 transition-colors border border-background-200/70"
                    >
                      <span className="text-sm text-foreground-800">{ch.title}</span>
                      <span className="text-xs text-foreground-400">{ch.updatedAt}</span>
                    </Link>
                  ))}
                </div>
                {comic.chapters.length > 10 && (
                  <button
                    onClick={() => setShowAllChapters(!showAllChapters)}
                    className="mt-4 text-sm text-primary-500 hover:text-primary-600 font-medium"
                  >
                    {showAllChapters ? '收起' : `展开全部 ${comic.chapters.length} 话`}
                  </button>
                )}
              </div>

              {related.length > 0 && (
                <div className="lg:w-72 flex-shrink-0">
                  <h2 className="text-lg font-heading font-bold text-foreground-950 mb-5">相关推荐</h2>
                  <div className="space-y-3">
                    {related.map((r) => (
                      <Link
                        key={r.id}
                        to={`/comic/${r.id}`}
                        className="flex items-center gap-3 p-3 rounded-lg bg-background-100 hover:bg-background-200/70 transition-colors"
                      >
                        <div className="w-12 h-16 rounded overflow-hidden flex-shrink-0 bg-background-200">
                          <img src={r.cover} alt={r.title} className="w-full h-full object-cover" />
                        </div>
                        <div className="min-w-0">
                          <p className="text-sm font-medium text-foreground-800 truncate">{r.title}</p>
                          <p className="text-xs text-foreground-500 mt-1">{r.author} · {r.rating}分</p>
                        </div>
                      </Link>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </>
  );
}